import React, { useState } from 'react';
import { Box, Button, TextField } from '@mui/material';
import BaseModal from '@/components/modals/BaseModal';
import {
  updatePrototypeFirmware,
  formatPrototypeFirmwareError,
} from '@/repository/PrototypeFirmwareDAO';
import {
  useToast,
  ToastType,
} from '@/context_providers/toast_context/ToastContext';
import type { PrototypeFirmware } from '@/models/PrototypeFirmware/PrototypeFirmware';

type Props = {
  firmware: PrototypeFirmware | null;
  onClose: () => void;
  onUpdated: () => Promise<void> | void;
};

// Parent remounts this modal via `key={editing?.id ?? 'closed'}`, so
// initializing state directly from props gives a clean prefill on every open
// without a setState-in-effect cascade.
const EditPrototypeFirmwareModal: React.FC<Props> = ({
  firmware,
  onClose,
  onUpdated,
}) => {
  const { showToast } = useToast();
  const [name, setName] = useState(firmware?.name ?? '');
  const [version, setVersion] = useState(firmware?.version ?? '');
  const [description, setDescription] = useState(firmware?.description ?? '');
  const [submitting, setSubmitting] = useState(false);

  const trimmedName = name.trim();
  const trimmedVersion = version.trim();
  const canSubmit =
    !!firmware &&
    trimmedName.length > 0 &&
    trimmedName.length <= 200 &&
    trimmedVersion.length > 0 &&
    trimmedVersion.length <= 50 &&
    description.length <= 2000 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !firmware) return;
    setSubmitting(true);
    try {
      await updatePrototypeFirmware(
        firmware.id,
        trimmedName,
        trimmedVersion,
        description.trim(),
      );
    } catch (error) {
      const { message, shouldRefetch } = formatPrototypeFirmwareError(
        error,
        'Firmware',
      );
      showToast(message, ToastType.DANGER);
      if (shouldRefetch) {
        try {
          await onUpdated();
        } catch {
          // parent's refetch toast covers this
        }
      }
      setSubmitting(false);
      return;
    }
    showToast(`Updated ${trimmedName} v${trimmedVersion}.`, ToastType.SUCCESS);
    try {
      await onUpdated();
    } catch {
      // onUpdated failures are surfaced by the parent's own refetch toast.
    }
    onClose();
    setSubmitting(false);
  };

  return (
    <BaseModal
      open={!!firmware}
      onClose={submitting ? () => {} : onClose}
      title={firmware ? `Edit ${firmware.name} v${firmware.version}` : 'Edit'}
      submitButtonText=""
      onSubmit={() => {}}
      enableEnterKey={false}
      showCloseButton={!submitting}
      footerContent={
        <>
          <Button onClick={onClose} disabled={submitting} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            Save
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 200))}
          error={name.length > 0 && trimmedName.length === 0}
          helperText={
            name.length > 0 && trimmedName.length === 0
              ? 'Name is required.'
              : ''
          }
          disabled={submitting}
          fullWidth
        />
        <TextField
          label="Version"
          required
          value={version}
          onChange={(e) => setVersion(e.target.value.slice(0, 50))}
          error={version.length > 0 && trimmedVersion.length === 0}
          helperText={
            version.length > 0 && trimmedVersion.length === 0
              ? 'Version is required.'
              : ''
          }
          disabled={submitting}
          fullWidth
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 2000))}
          multiline
          minRows={3}
          maxRows={8}
          disabled={submitting}
          fullWidth
        />
      </Box>
    </BaseModal>
  );
};

export default EditPrototypeFirmwareModal;
